"use client";

import React from "react";
import { useDispatch, useSelector } from "react-redux";
import "./practitioner-details.css";
import { Star, ArrowLeft, X, ExternalLink, Share2 } from "lucide-react";
import useIsMobile from "../shared/Mobile-Responsive";
import { Image } from "antd";
import IMAGES from "../../../../constant/images";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { updatePractitionerStatus } from "../../api/Practitioner/PractitionerDetail";

const PractitionerDetail = ({ practitionerData, onBack, onClose, onShare, onExport }) => {
  const dispatch = useDispatch();
  const { updating } = useSelector((state) => state.practitionerDetail);
  const [status, setStatus] = React.useState(practitionerData?.status === "ACTIVE" ? "active" : "inactive");
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const isMobile = useIsMobile();

  // Sync status with practitionerData when it changes
  React.useEffect(() => {
    if (practitionerData?.status) {
      setStatus(practitionerData.status === "ACTIVE" ? "active" : "inactive");
    }
  }, [practitionerData?.status]);

  // Handle status toggle
  const handleStatusToggle = async () => {
    if (!practitionerData?.pcUserId || updating) return;

    const newStatus = status === "active" ? "inactive" : "active";
    const apiStatus = newStatus === "active" ? "ACTIVE" : "INACTIVE";

    try {
      await dispatch(updatePractitionerStatus({
        pcUserId: practitionerData.pcUserId,
        status: apiStatus
      })).unwrap();
      setStatus(newStatus);
    } catch (error) {
      // Error already handled in the slice
      console.error("Failed to update status:", error);
    }
  };

  const handleSaveFile = () => {
    if (!practitionerData) return;

    try {
      const exportData = [
        {
          "Practitioner ID": practitionerData.pcUserId || "",
          "Name": `${practitionerData.title || ""} ${practitionerData.firstName || ""} ${practitionerData.lastName || ""}`.trim(),
          "Email": practitionerData.email || "",
          "Phone": practitionerData.mobile || "",
          "ABHA Number": practitionerData.abhaNumber || "",
          "Role": practitionerData.counsellorRole || practitionerData.role || "",
          "Languages": practitionerData.languages || "",
          "Degree": practitionerData.degree?.join(", ") || "",
          "About": practitionerData.about || "",
          "State": practitionerData.state || "",
          "City": practitionerData.city || "",
          "Country": practitionerData.country || "",
          "Pincode": practitionerData.pincode || "",
          "Organization Name": practitionerData.organisationName || "",
          "Organization Location": practitionerData.organisationLocation || "",
          "Organization City": practitionerData.organisationCity || "",
          "Organization State": practitionerData.organisationState || "",
          "Total Appointments": practitionerData.totalAppointments || 0,
          "Completed Appointments": practitionerData.completedAppointments || 0,
          "Follow Ups": practitionerData.followUps || 0,
          "Prescriptions Issued": practitionerData.prescriptionIssued || 0,
          "Status": practitionerData.status || "",
          "Last Access Time": practitionerData.lastAccessTime || "",
          "Created On": practitionerData.createdOn || "",
          "Modified On": practitionerData.modifiedOn || "",
        },
      ];

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Practitioner Details");
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `practitioner_${practitionerData.pcUserId || 'details'}.xlsx`);
    } catch (error) {
      console.error("Error exporting practitioner details:", error);
    }
  };

  const handleEmailShare = () => {
    handleSaveFile(); // still downloads the XLSX locally

    const subject = encodeURIComponent("Practitioner Details");
    const body = encodeURIComponent(
      "Hi,\n\nPlease find the attached practitioner details file.\n\nThanks!"
    );

    // Opens user's default email app (Gmail, Outlook, etc.)
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const sessionHistory = [
    {
      date: "10 Apr 2025",
      title: "1st Consultation",
      doctor: "Dr. Tanishka Rai",
      status: "Completed",
      type: "Audio",
    },
    {
      date: "12 Apr 2025",
      title: "2st Consultation",
      doctor: "Dr. Tanishka Rai",
      status: "Completed",
      type: "Audio",
    },
    {
      date: "13 Apr 2025",
      title: "3rd Consultation",
      doctor: "Dr. Tanishka Rai",
      status: "Completed",
      type: "Audio",
    },
    {
      date: "15 Apr 2025",
      title: "4th Consultation",
      doctor: "Dr. Tanishka Rai",
      status: "Upcoming",
      type: "In Person",
    },
  ];

  // Get ratings data from API or use empty array
  const ratingsData = practitionerData?.latestRatings || [];

  /// Use averageRating from API
  const overallRating = practitionerData?.averageRating || 0;

  // Helper function to render star rating (out of 5)
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        // Full star
        stars.push(
          <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
        );
      } else if (i === fullStars && hasHalfStar) {
        // Half star
        stars.push(
          <div key={i} className="relative w-4 h-4">
            <Star className="w-4 h-4 text-yellow-400" />
            <div className="absolute top-0 left-0 w-1/2 overflow-hidden">
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
            </div>
          </div>
        );
      } else {
        // Empty star
        stars.push(
          <Star key={i} className="w-4 h-4 text-[#CBD5E1]" />
        );
      }
    }
    return stars;
  };

  // Format date for display
  const formatRatingDate = (dateString) => {
    if (!dateString) return "Date not available";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return "Date not available";
    }
  };

  // Early return if no data
  if (!practitionerData) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-sm text-[#64748B] font-poppins">No practitioner data available</p>
      </div>
    );
  }

  return (
    <>
      {isMobile ? (
        <div className="font-poppins bg-white min-h-screen mx-auto">
          {/* Mobile Header */}
          <div className="bg-white px-2 py-4 flex items-center justify-between border-b-[1px] border-solid border-x-0 border-t-0 border-[#F1F5F9]">
            <div className="flex items-center gap-3">
              <button onClick={onBack} className="flex items-center justify-center w-9 h-9 bg-white text-[#171717] border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm hover:bg-[#F8FAFC]">
                <ArrowLeft size={18} />
              </button>
              <h1 className="text-xl font-bold text-[#171717] font-poppins m-0">Practitioner Details</h1>
            </div>
            <div className="header-actions flex items-center gap-2 relative">
              <Image
                src={IMAGES.external_icon}
                alt="Export"
                width={40}
                height={40}
                preview={false}
                className="cursor-pointer"
                onClick={handleSaveFile}
              />

              {/* Share dropdown toggle */}
              <div className="relative mt-1">
                <Image
                  src={IMAGES.share_icon}
                  alt="Share"
                  width={40}
                  height={40}
                  preview={false}
                  className="cursor-pointer"
                  onClick={() => setIsDropdownOpen(prev => !prev)}
                />

                {isDropdownOpen && (
                  <div
                    className="absolute right-0 mt-2 w-40 py-1 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-lg overflow-hidden z-[9999]"
                  >
                    <button
                      className="block w-full text-left px-4 py-2 text-sm text-[#334155] font-poppins bg-white border-0 hover:bg-[#F8FAFC]"
                      onClick={handleEmailShare}
                    >
                      Share on Email
                    </button>
                    <button
                      className="block w-full text-left px-4 py-2 text-sm text-[#334155] font-poppins bg-white border-0 hover:bg-[#F8FAFC]"
                      onClick={handleSaveFile}
                    >
                      Save as File
                    </button>
                  </div>
                )}
              </div>

              <button className="action-button ml-2" onClick={onClose}>
                <svg
                  width="30"
                  height="30"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="m18 6-12 12" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Content */}
          <div className="p-4 space-y-4">
            {/* Basic Information */}
            <div className="bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl p-4 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base font-semibold text-[#171717] font-poppins mb-0">Basic Information</h3>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#64748B]">Status</span>
                  <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
                    status === "active" ? "bg-[#ECFDF5] text-[#059669]" : "bg-[#FEF2F2] text-[#DC2626]"
                  }`}>
                    {status === "active" ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-[#64748B]">Name</span>
                  <span className="text-sm text-[#171717]">{practitionerData?.title} {practitionerData?.firstName} {practitionerData?.lastName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-[#64748B]">User ID</span>
                  <span className="text-sm text-[#171717]">{practitionerData?.pcUserId || "Not Available"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-[#64748B]">Email</span>
                  <span className="text-sm text-[#171717]">{practitionerData?.email || "Not Available"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-[#64748B]">Phone</span>
                  <span className="text-sm text-[#171717]">{practitionerData?.mobile || "Not Available"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-[#64748B]">ABHA Number</span>
                  <span className="text-sm text-[#171717]">{practitionerData?.abhaNumber || "Not Available"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-[#64748B]">Role</span>
                  <span className="text-sm text-[#171717]">{practitionerData?.counsellorRole || practitionerData?.role || "Not Available"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-[#64748B]">Languages</span>
                  <span className="text-sm text-[#171717]">{practitionerData?.languages || "Not specified"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-[#64748B]">Degree</span>
                  <span className="text-sm text-[#171717]">{practitionerData?.degree?.length > 0 ? practitionerData.degree.join(", ") : "Not Available"}</span>
                </div>
              </div>
            </div>

            {/* About Section */}
            <div className="bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl p-4 shadow-sm">
              <h3 className="text-base font-semibold text-[#171717] font-poppins mb-3">About</h3>
              <p className="text-sm text-[#334155] mb-0">{practitionerData?.about || "Not Available"}</p>
            </div>

            {/* Overview Stats */}
            <div className="bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl p-4 shadow-sm">
              <h3 className="text-base font-semibold text-[#171717] font-poppins mb-4">Overview Stats</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-[26px] leading-tight font-bold text-[#171717] font-poppins">{practitionerData?.totalAppointments || 0}</div>
                  <div className="text-[12px] uppercase tracking-wide font-medium text-[#64748B]">Total Appointments</div>
                </div>
                <div className="text-center">
                  <div className="text-[26px] leading-tight font-bold text-[#171717] font-poppins">{practitionerData?.completedAppointments || 0}</div>
                  <div className="text-[12px] uppercase tracking-wide font-medium text-[#64748B]">Completed</div>
                </div>
                <div className="text-center">
                  <div className="text-[26px] leading-tight font-bold text-[#171717] font-poppins">{practitionerData?.followUps || 0}</div>
                  <div className="text-[12px] uppercase tracking-wide font-medium text-[#64748B]">Follow-ups</div>
                </div>
                <div className="text-center">
                  <div className="text-[26px] leading-tight font-bold text-[#171717] font-poppins">{practitionerData?.prescriptionIssued || 0}</div>
                  <div className="text-[12px] uppercase tracking-wide font-medium text-[#64748B]">Prescriptions</div>
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl p-4 shadow-sm">
              <h3 className="text-base font-semibold text-[#171717] font-poppins mb-4">Address Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-[#64748B]">State</span>
                  <span className="text-sm text-[#171717]">{practitionerData?.state || "Not Available"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-[#64748B]">City</span>
                  <span className="text-sm text-[#171717]">{practitionerData?.city || "Not Available"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-[#64748B]">Country</span>
                  <span className="text-sm text-[#171717]">{practitionerData?.country || "Not Available"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-[#64748B]">Pincode</span>
                  <span className="text-sm text-[#171717]">{practitionerData?.pincode || "Not Available"}</span>
                </div>
              </div>
            </div>

            {/* Organization Information */}
            <div className="bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl p-4 shadow-sm">
              <h3 className="text-base font-semibold text-[#171717] font-poppins mb-4">Organization Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-[#64748B]">Organization Name</span>
                  <span className="text-sm text-[#171717]">{practitionerData?.organisationName || "Not Available"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-[#64748B]">Location</span>
                  <span className="text-sm text-[#171717]">{practitionerData?.organisationLocation || "Not Available"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-[#64748B]">City</span>
                  <span className="text-sm text-[#171717]">{practitionerData?.organisationCity || "Not Available"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-[#64748B]">State</span>
                  <span className="text-sm text-[#171717]">{practitionerData?.organisationState || "Not Available"}</span>
                </div>
              </div>
            </div>

            {/* Rating & Feedback - Mobile */}
            <div className="bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl p-4 shadow-sm">
              <h3 className="text-base font-semibold text-[#171717] font-poppins mb-4">Rating & Feedback</h3>
              <div className="flex flex-col gap-2 mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#64748B]">Overall Rating:</span>
                  <div className="flex items-center gap-1">
                    {renderStars(overallRating)}
                  </div>
                  <span className="text-sm font-semibold text-[#171717]">
                    {overallRating > 0 ? `${overallRating.toFixed(1)} / 5` : "No ratings"}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                {ratingsData && ratingsData.length > 0 ? (
                  ratingsData.map((rating, index) => (
                    <div
                      key={index}
                      className="bg-[#F8FAFC] border-[1px] border-solid border-[#E2E8F0] rounded-xl p-3"
                    >
                      {/* Rating and Author */}
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-semibold text-[#171717]">
                          {rating.userName || "Anonymous"}
                        </span>
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          <span className="text-sm font-medium text-[#334155]">
                            {rating.rating || "N/A"}
                          </span>
                        </div>
                      </div>

                      {/* Feedback Text */}
                      <p className="text-sm text-[#334155] mb-2">
                        {rating.ratingText || "No feedback provided"}
                      </p>

                      {/* Date */}
                      <span className="text-xs text-[#64748B]">
                        {formatRatingDate(rating.ratingDate)}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="bg-[#F8FAFC] border-[1px] border-solid border-[#E2E8F0] rounded-xl p-4 text-center">
                    <p className="text-sm text-[#64748B] mb-0">No ratings or feedback available yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="member-details-container">
          <div className="member-details-wrapper">
            {/* Desktop Header */}
            <div className="header">
              <div className="header-left">
                <button className="back-button" onClick={onBack}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                </button>
                <h1 className="page-title">Practitioner Details</h1>
              </div>
              <div className="header-actions flex items-center gap-2 relative">
                <Image
                  src={IMAGES.external_icon}
                  alt="Export"
                  width={40}
                  height={40}
                  preview={false}
                  className="cursor-pointer"
                  onClick={handleSaveFile}
                />

                {/* Share dropdown toggle */}
                <div className="relative mt-1">
                  <Image
                    src={IMAGES.share_icon}
                    alt="Share"
                    width={40}
                    height={40}
                    preview={false}
                    className="cursor-pointer"
                    onClick={() => setIsDropdownOpen(prev => !prev)}
                  />

                  {isDropdownOpen && (
                    <div
                      className="absolute right-0 mt-2 w-40 py-1 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-lg overflow-hidden z-[9999]"
                    >
                      <button
                        className="block w-full text-left px-4 py-2 text-sm text-[#334155] font-poppins bg-white border-0 hover:bg-[#F8FAFC]"
                        onClick={handleEmailShare}
                      >
                        Share on Email
                      </button>
                      <button
                        className="block w-full text-left px-4 py-2 text-sm text-[#334155] font-poppins bg-white border-0 hover:bg-[#F8FAFC]"
                        onClick={handleSaveFile}
                      >
                        Save as File
                      </button>
                    </div>
                  )}
                </div>

                <button className="action-button ml-2" onClick={onClose}>
                  <svg
                    width="30"
                    height="30"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="m18 6-12 12" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
              </div>
            </div>

        <div className="main-content">
          {/* Left Column */}
          <div className="left-section">
            {/* Basic Information Section */}
            <div className="section">
              <div className="section-header">
                <h3 className="section-title">Basic Information</h3>
                {/* Status Toggle */}
                <div className="status-toggle">
                  <span className="status-label">Status</span>
                  <div className="switch-container">
                    <span
                      className={`switch-text left ${
                        status === "inactive" ? "active" : ""
                      }`}
                    >
                      Inactive
                    </span>
                    <div
                      className="switch-track"
                      onClick={handleStatusToggle}
                      style={{ opacity: updating ? 0.6 : 1, cursor: updating ? 'not-allowed' : 'pointer' }}
                    >
                      <div
                        className={`switch-thumb ${
                          status === "active" ? "active" : ""
                        }`}
                      ></div>
                    </div>
                    <span
                      className={`switch-text right ${
                        status === "active" ? "active" : ""
                      }`}
                    >
                      Active
                    </span>
                  </div>
                </div>
              </div>

              <div className="info-list">
                <div className="info-item">
                  <span className="label">Name</span>
                  <span className="value">{practitionerData?.title} {practitionerData?.firstName} {practitionerData?.lastName}</span>
                </div>
                <div className="info-item">
                  <span className="label">User ID</span>
                  <span className="value">{practitionerData?.pcUserId}</span>
                </div>
                <div className="info-item">
                  <span className="label">Email ID</span>
                  <span className="value">{practitionerData?.email}</span>
                </div>
                <div className="info-item">
                  <span className="label">Phone Number</span>
                  <span className="value">{practitionerData?.mobile}</span>
                </div>
                <div className="info-item">
                  <span className="label">ABHA Number</span>
                  <span className="value">{practitionerData?.abhaNumber || "Not Available"}</span>
                </div>
                <div className="info-item">
                  <span className="label">Role</span>
                  <span className="value">{practitionerData?.counsellorRole || practitionerData?.role || "Not Available"}</span>
                </div>
                <div className="info-item">
                  <span className="label">Languages Spoken</span>
                  <span className="value">{practitionerData?.languages || "Not specified"}</span>
                </div>
                <div className="info-item">
                  <span className="label">Degree</span>
                  <span className="value">{practitionerData?.degree?.join(", ") || "Not specified"}</span>
                </div>
                <div className="info-item">
                  <span className="label">About Yourself</span>
                  <span
                    className="value"
                    style={{
                      textAlign: "right",
                      width: "15% !important",
                    }}
                  >
                    {practitionerData?.about || "No description available"}
                  </span>
                </div>
              </div>
            </div>

            {/* Emergency Contact Details */}
            <div className="section">
              <h3 className="section-title">Address Information</h3>
              <div className="info-list" style={{ marginTop: "16px" }}>
                <div className="info-item">
                  <span className="label">State</span>
                  <span className="value ">{practitionerData?.state || "Not specified"}</span>
                </div>
                <div className="info-item">
                  <span className="label">City</span>
                  <span className="value">{practitionerData?.city || "Not specified"}</span>
                </div>
                <div className="info-item">
                  <span className="label">Country</span>
                  <span className="value">{practitionerData?.country || "Not specified"}</span>
                </div>
                <div className="info-item">
                  <span className="label">Pincode</span>
                  <span className="value">{practitionerData?.pincode || "Not specified"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="right-section">
            {/* Overview Stats */}
            <div className="section">
              <h3 className="section-title">Overview Stats</h3>
              <div
                className="stats-list"
                style={{
                  border: "1px solid #E2E8F0",
                  borderRadius: "16px",
                  padding: "16px",
                  marginTop: "24px",
                }}
              >
                <div className="stat-item">
                  <span className="stat-label">Total Appointments</span>
                  <span className="stat-value">{practitionerData?.totalAppointments || 0}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Completed</span>
                  <span className="stat-value">{practitionerData?.completedAppointments || 0}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Follow-ups</span>
                  <span className="stat-value">{practitionerData?.followUps || 0}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Prescriptions Issued</span>
                  <span className="stat-value">{practitionerData?.prescriptionIssued || 0}</span>
                </div>
              </div>
            </div>

            {/* Organizational Information */}
            <div className="section">
              <h3 className="section-title">Organizational Information</h3>
              <div className="info-list" style={{ marginTop: "16px" }}>
                <div className="info-item">
                  <span className="label">Organization Name</span>
                  <span className="value ">{practitionerData?.organisationName || "Not specified"}</span>
                </div>
                <div className="info-item">
                  <span className="label">Organization Location</span>
                  <span className="value">{practitionerData?.organisationLocation || "Not specified"}</span>
                </div>
                <div className="info-item">
                  <span className="label">Organization City</span>
                  <span className="value">{practitionerData?.organisationCity || "Not specified"}</span>
                </div>
                <div className="info-item">
                  <span className="label">Organization State</span>
                  <span className="value">{practitionerData?.organisationState || "Not specified"}</span>
                </div>
              </div>
            </div>

            {/* Session History */}
            <div
              className="section"
              style={{
                border: "1px solid #E2E8F0",
                borderRadius: "16px",
                padding: "16px",
                marginTop: "24px",
              }}
            >
              <div className="flex justify-between items-start">
                <h3 className="section-title">Rating & Feedback</h3>
                <div className="flex flex-col items-end gap-1">
                  <span className="text-[12px] text-[#64748B]">Overall Rating</span>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      {renderStars(overallRating)}
                    </div>
                    <span className="text-[14px] text-[#171717] font-semibold">
                      {overallRating > 0 ? `${overallRating.toFixed(1)} / 5` : "No ratings"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="w-full mt-4 space-y-4">
                {ratingsData && ratingsData.length > 0 ? (
                  ratingsData.map((rating, index) => (
                    <div
                      key={index}
                      className="bg-[#FFFFFF] border-[1px] border-solid border-[#E2E8F0] rounded-2xl p-3 relative shadow-sm"
                    >
                      {/* Star Rating - Top Right */}
                      <div className="absolute top-6 right-6 flex items-center gap-1">
                        <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                        <span className="text-[#334155] font-medium">
                          {rating.rating || "N/A"}
                        </span>
                      </div>

                      {/* Review Content */}
                      <div className="pr-16 mb-6">
                        <p className="text-[#334155] text-[14px] leading-relaxed">
                          {rating.ratingText || "No feedback provided"}
                        </p>
                      </div>

                      {/* Author and Date - Bottom */}
                      <div className="flex justify-between items-center">
                        <h3 className="text-[#171717] font-semibold text-[16px]">
                          {rating.userName || "Anonymous"}
                        </h3>
                        <span className="text-[#64748B] text-[12px]">
                          {formatRatingDate(rating.ratingDate)}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="bg-[#F8FAFC] border-[1px] border-solid border-[#E2E8F0] rounded-2xl p-6 text-center">
                    <p className="text-[#64748B] text-[14px] mb-0">No ratings or feedback available yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PractitionerDetail;
